import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import {Button, Stack} from '@mui/material';
import {Add, Alert, Comment} from '@icons/index';
import {
  Execution,
  ExecutionLight,
  executionsQuery,
  executionsService,
  ExecutionStatus,
} from '@store/operations/executions';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {finalize} from 'rxjs';
import {useObservable} from '@ngneat/react-rxjs';

interface LinkedExecutionModalProps {
  executionId: string | number;
  parentExecutionId: number | undefined;
  handleClose: () => void;
}

const LinkedExecutionModal = (props: LinkedExecutionModalProps) => {
  const {executionId, parentExecutionId, handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [executions, setExecutions] = useState<ExecutionLight[]>([]);
  const [{loading: linkLoading}] = useObservable(executionsQuery.linkedExecutions$);

  useEffect(() => {
    if (executionId) {
      setLoading(true);
      executionsService
        .getLinkedExecutionById(executionId)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: setExecutions,
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  }, [executionId, enqueueSnackbar]);

  useEffect(() => {
    if (executionId && linkLoading) {
      setLoading(true);
      executionsService
        .getLinkedExecutionById(executionId)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: setExecutions,
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  }, [executionId, linkLoading, enqueueSnackbar]);

  return (
    <ModalComponent
      title={t('operations.executions.linkedExecutionModal.title')}
      aria-label="over-drawer"
      handleClose={handleClose}
      maxWidth={false}
      content={
        <Stack pt="20px" height="65vh" width="100%" spacing={2} overflow="hidden">
          <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                navigate('add-execution', {
                  state: {parentId: parentExecutionId ? parentExecutionId : executionId},
                })
              }
              startIcon={<Add/>}
            >
              {t('operations.addButtons.execution')}
            </Button>
          </Stack>
          <NewTableComponent
            loading={loading}
            columns={[
              COLUMN_TYPES.TEXT({
                field: 'execution.createdAt',
                color: 'secondary',
                label: t('operations.executions.table.createdAt'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.createdAt,
                valueFormatter: (params) =>
                  params.value ? t('global.dateAndHour', {date: new Date(params.value)}) : '',
                sortable: true,
                width: 140,
              }),
              COLUMN_TYPES.TEXT({
                field: 'execution.plannedFrom',
                color: 'secondary',
                label: t('operations.executions.table.plannedFrom'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.plannedFrom,
                valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : ''),
                sortable: true,
                width: 110,
              }),
              COLUMN_TYPES.TEXT({
                field: 'execution.executedAt',
                color: 'secondary',
                label: t('operations.executions.table.executedAt'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.executedAt,
                valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : ''),
                sortable: true,
                width: 110,
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'execution.generatedByScheduleID',
                label: t('operations.executions.table.creationType'),
                valueGetter: (params: GridValueGetterParams<any, Execution>) =>
                  params.row.recurring ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                sortable: true,
                width: 110,
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'service.kind',
                label: t('operations.executions.table.type'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.type,
                sortable: true,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste.name',
                color: 'secondary',
                label: t('operations.executions.table.waste'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.waste,
                sortable: true,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container.name',
                color: 'secondary',
                label: t('operations.executions.table.container'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.container,
                sortable: true,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'organisationUnit.name',
                color: 'secondary',
                label: t('operations.executions.table.client'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) =>
                  params.row.clientOrganisationUnit?.name,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'supOrgUnit.name',
                color: 'secondary',
                label: t('operations.executions.table.supplier'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) =>
                  params.row.supplierOrganisationUnit?.name,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.NUMBER({
                field: 'execution.quantity1',
                label: t('operations.executions.table.quantity'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.quantity1,
                width: 100,
              }),
              COLUMN_TYPES.TEXT({
                field: 'serviceKind.execUnitName1',
                color: 'secondary',
                label: t('operations.executions.table.unit'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.unit1,
                width: 100,
              }),
              COLUMN_TYPES.SELECT({
                field: 'execution.status',
                label: t('global.status'),
                options: ExecutionStatus.selectItems(),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.status,
                width: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'esh.createdAt',
                color: 'secondary',
                label: t('operations.executions.table.statusLastUpdatedAt'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.updatedAt,
                valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : ''),
                width: 130,
              }),
              COLUMN_TYPES.ICONS({
                field: 'properties',
                label: '',
                icons: [
                  {
                    icon: <Comment style={{width: 18, marginTop: 5, height: 21}}/>,
                    hidden: (execution: ExecutionLight) => (execution.commentsCount ?? 0) <= 0,
                  },
                  {
                    icon: <Alert style={{height: 18, width: 21}}/>,
                    hidden: (execution: ExecutionLight) => (execution.issueCount ?? 0) <= 0,
                  },
                ],
                width: 60,
              }),
              COLUMN_TYPES.ACTIONS({
                handleShowRow: (execution: ExecutionLight) => navigate(`../${execution.id}`),
              }),
            ]}
            disableRowSelection
            paginationMode="client"
            disablePagination
            data={executions}
          />
        </Stack>
      }
    />
  );
};

export default LinkedExecutionModal;
